import envVars from 'helpers/envVars';
import request from 'helpers/request';
import buildHTTPResponseHandler from 'helpers/buildHTTPResponseHandler';

import * as DTO from './dto';

const SERVICE_URI = envVars('API_COPILOT_BASE_URL');
const SERVICE_CONTENT_URI = envVars('API_COPILOT_CONTENT_URL');

const SERVICE_NAME = 'copilot';
const SERVICE_CONTENT_NAME = 'copilotContent';

export async function getContent({ dealerId, contentId }: DTO.GetContentQ) {
  const response = await request
    .get(
      `${SERVICE_CONTENT_URI}/dealer/${dealerId}/content/${contentId}/file`,
      null,
      { responseType: 'blob' }
    )
    .then((response: any) => {
      buildHTTPResponseHandler(`${SERVICE_CONTENT_NAME}.getContent()`);

      const blob = new Blob([response?.data], { type: 'image/png' });
      const blobUrl = window.URL.createObjectURL(blob);

      return blobUrl;
    });

  return response;
}

function extractDealerAndContent(url: string) {
  const regex = /dealer\/(\d+)\/content\/(\d+)/;
  const match = url.match(regex);

  if (match) {
    return { dealerId: match[1], contentId: match[2] };
  }

  return null;
}

async function formatMessages(values: DTO.GetMessageByIdR) {
  const currentValues = { ...values };
  const currentContents = [...currentValues?.contents];

  const currentImages = currentContents?.filter(value => {
    return value.mimetype === 'image/png';
  });

  await Promise.all(
    currentImages.map(async value => {
      if (!value?.url) return;

      const { contentId, dealerId } = extractDealerAndContent(value.url);

      const imageData = await getContent({
        contentId,
        dealerId,
      });

      const indexToReplace = currentValues.contents.findIndex(({ id }) => {
        return id === value.id;
      });

      if (indexToReplace < 0) return;

      currentContents[indexToReplace].url = imageData;
    })
  );

  currentValues.contents = [...currentContents];

  return currentValues;
}

export async function getMessageById({
  dealerId,
  messageId,
}: DTO.GetMessageByIdQ) {
  const response = await request
    .get<DTO.GetMessageByIdR>(
      `${SERVICE_URI}/dealer/${dealerId}/message/${messageId}`
    )
    .then(response => {
      buildHTTPResponseHandler(`${SERVICE_NAME}.getMessageById()`);
      return response?.data;
    });

  return formatMessages(response);
}

export async function getMessagesHistory({
  dealerId,
}: DTO.GetMessagesHistoryQ) {
  const response = await request
    .get<DTO.GetMessagesHistoryR>(`${SERVICE_URI}/dealer/${dealerId}/messages`)
    .then(response => {
      buildHTTPResponseHandler(`${SERVICE_NAME}.getMessagesHistory()`);
      return response?.data;
    });

  const formatted = await Promise.all(
    response?.map(value => formatMessages(value))
  );

  return formatted;
}

export async function sendMessage({ dealerId, message }: DTO.SendMessageQ) {
  const formData = new FormData();

  formData.append('message', message);

  const response = await request
    .post<DTO.SendMessageR>(
      `${SERVICE_URI}/dealer/${dealerId}/message`,
      formData
    )
    .then(response => {
      buildHTTPResponseHandler(`${SERVICE_NAME}.sendMessage()`);
      return response?.data;
    });

  return response;
}

export async function getMessageSuggestions({
  dealerId,
}: DTO.GetMessageSuggestionsQ) {
  const response = await request
    .get<DTO.GetMessageSuggestionsR>(
      `${SERVICE_URI}/dealer/${dealerId}/suggestion`
    )
    .then(response => {
      buildHTTPResponseHandler(`${SERVICE_NAME}.getMessageSuggestions()`);
      return response?.data;
    });

  return response;
}

export async function resetChat({ dealerId }: DTO.ResetChatQ) {
  const response = await request
    .post(`${SERVICE_URI}/dealer/${dealerId}/reset`)
    .then(response => {
      buildHTTPResponseHandler(`${SERVICE_NAME}.resetChat()`);
      return response?.status === 200;
    });

  return response;
}
